// ─────────────────────────────────────────────────────────────────────────────
// chartMath.js — the curve + axis maths every hand-rolled SVG chart on this app
// shares. It used to be copy-pasted verbatim into Dashboard.js and Analytics.js,
// which is how two charts that are supposed to look identical quietly stop
// looking identical.
//
// Nothing here touches React or the DOM — pure functions over numbers.
// ─────────────────────────────────────────────────────────────────────────────

// ── d3-shape curveMonotoneX (Fritsch–Carlson monotone cubic) ─────────────────
// A smooth line that never overshoots its data points. This reproduces the
// Figma curves' Bézier control points exactly — the designs were exported from
// a monotone-X line chart, so any other interpolation visibly disagrees with
// them at the peaks.

const sign = (x) => (x < 0 ? -1 : 1);

function slope3(x0, y0, x1, y1, x2, y2) {
  const h0 = x1 - x0, h1 = x2 - x1;
  const s0 = (y1 - y0) / h0, s1 = (y2 - y1) / h1;
  const p = (s0 * h1 + s1 * h0) / (h0 + h1);
  return (sign(s0) + sign(s1)) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p)) || 0;
}

function slope2(x0, y0, x1, y1, t) {
  const h = x1 - x0;
  return h ? (3 * (y1 - y0)) / h / 2 - t / 2 : t;
}

/**
 * Monotone-cubic path through the points (xs[i], ys[i]).
 * @param   {number[]} xs
 * @param   {number[]} ys  same length as xs
 * @returns {string}       an SVG path `d`, or "" for fewer than 2 points
 */
export function monotoneXPath(xs, ys) {
  const n = xs.length;
  if (n < 2) return "";

  const t = new Array(n);
  if (n === 2) {
    t[0] = t[1] = (ys[1] - ys[0]) / (xs[1] - xs[0]);
  } else {
    for (let i = 1; i < n - 1; i++) {
      t[i] = slope3(xs[i - 1], ys[i - 1], xs[i], ys[i], xs[i + 1], ys[i + 1]);
    }
    t[0]     = slope2(xs[0], ys[0], xs[1], ys[1], t[1]);
    t[n - 1] = slope2(xs[n - 2], ys[n - 2], xs[n - 1], ys[n - 1], t[n - 2]);
  }

  const r = (v) => Math.round(v * 1000) / 1000;
  let d = `M ${r(xs[0])} ${r(ys[0])}`;
  for (let i = 0; i < n - 1; i++) {
    const h = (xs[i + 1] - xs[i]) / 3;
    d += ` C ${r(xs[i] + h)} ${r(ys[i] + t[i] * h)}, ${r(xs[i + 1] - h)} ${r(ys[i + 1] - t[i + 1] * h)}, ${r(xs[i + 1])} ${r(ys[i + 1])}`;
  }
  return d;
}

/**
 * Closes a line path into a filled area by dropping to the baseline.
 * @param {string}   line    the `d` returned by monotoneXPath (may be "")
 * @param {number[]} xs
 * @param {number}   baseline  y of the plot floor
 */
export function areaPath(line, xs, baseline) {
  if (!line) return "";
  return `${line} L ${xs[xs.length - 1]} ${baseline} L ${xs[0]} ${baseline} Z`;
}

// ── Axis ceilings ────────────────────────────────────────────────────────────

/**
 * Smallest "nice" axis ceiling >= max whose quarter-steps are round numbers, at
 * ANY magnitude — so the 5 y-ticks stay legible for small decimals as well as
 * large counts (0.4 → 0.4 in 0.1 steps; 54 → 80 in 20s; 553 → 800 in 200s;
 * 1847 → 2000 in 500s).
 */
export function niceCeiling(max) {
  if (!(max > 0)) return 1;
  const rough = max / 4;                                    // target step for 4 divisions
  const pow   = Math.pow(10, Math.floor(Math.log10(rough)));
  const n     = rough / pow;                                // 1 … <10
  const step  = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * pow;
  return step * 4;
}

/**
 * Ceiling picked from an explicit ladder — used where the design fixed the set
 * of axis maxima rather than deriving them. Falls back to the next round
 * thousand above the ladder's top.
 */
export function presetCeiling(max, presets) {
  const clean = max || 1;
  return presets.find((v) => v >= clean) ?? Math.ceil(clean / 1000) * 1000;
}

/** Tick label: up to 3 decimals, no trailing zeros, no thousands separator. */
export function formatTick(v) {
  return String(parseFloat(v.toFixed(3)));
}
